import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import {
  getAuth,
  onAuthStateChanged,
  setPersistence,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { resetClaimSync } from '../services/api';
import {
  clearAuthenticatedUserContext,
  setAuthenticatedUserContext,
  trackEvent,
  trackException,
} from '../utils/appInsights';
import {
  mixpanelIdentify,
  mixpanelPeopleSet,
  mixpanelReset,
  mixpanelTrack,
} from '../utils/mixpanel';

export interface AuthContextType {
  token: string | null;
  email: string | null;
  signUp: (email: string, pass: string) => Promise<void>;
  signIn: (email: string, pass: string) => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  loading: boolean;
  error: any;
}

// const SESSION_TIMEOUT_MS = 60 * 1000; // 1 minute for demo purposes; adjust as needed for production

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const auth = getAuth();
  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  // const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // useEffect(() => {
  //   if (!token) return;

  //   idleTimerRef.current = setTimeout(async () => {
  //     trackEvent('User_AutoSignOut_SessionExpired', { email: email || 'unknown' });
  //     await fbSignOut(auth);
  //     setToken(null);
  //     setEmail(null);
  //     clearAuthenticatedUserContext();
  //     resetClaimSync();
  //   }, SESSION_TIMEOUT_MS);

  //   return () => {
  //     if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
  //   };
  // }, [token]);

  useEffect(() => {
    // force sessionStorage persistence in web…
    setPersistence(auth, browserSessionPersistence).catch(console.error);

    // listen for the Firebase Auth state
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const t = await user.getIdToken(true);
        setToken(t);
        setEmail(user.email);

        // Attach a stable, non-PII identifier to all browser telemetry.
        // This enables per-authenticated-user session/journey views in Application Insights.
        setAuthenticatedUserContext(user.uid);
        // Re-identify the user in Mixpanel on every app re-open (already logged in).
        mixpanelIdentify(user.uid);
      } else {
        setToken(null);
        setEmail(null);

        // Clear the user context so subsequent anonymous telemetry doesn't inherit it.
        clearAuthenticatedUserContext();
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [auth]);

  const signUp = async (emailArg: string, pass: string) => {
    setLoading(true);
    setError(null);
    try {
      const userCred = await createUserWithEmailAndPassword(auth, emailArg, pass);
      const t = await userCred.user.getIdToken();
      setToken(t);
      setEmail(userCred.user.email);

      setAuthenticatedUserContext(userCred.user.uid);

      // Identify user in Mixpanel, set profile, then track sign_up_completed.
      mixpanelIdentify(userCred.user.uid);
      mixpanelPeopleSet({ $email: emailArg, platform: 'web' });
      mixpanelTrack('sign_up_completed', { sign_up_method: 'email'});
      
      // Track successful sign-up
      trackEvent('User_SignUp_Success', { 
        email: emailArg
      });
    } catch (e: any) {
      setError(e);
      
      // Track sign-up failure
      trackEvent('User_SignUp_Failed', { 
        email: emailArg,
        errorCode: e.code,
        errorMessage: e.message
      });
      trackException(e, { context: 'signUp' });
      
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (emailArg: string, pass: string) => {
    setLoading(true);
    setError(null);
    try {
      const userCred = await signInWithEmailAndPassword(auth, emailArg, pass);
      const t = await userCred.user.getIdToken();
      setToken(t);
      setEmail(userCred.user.email);

      setAuthenticatedUserContext(userCred.user.uid);

      // Identify returning user in Mixpanel.
      mixpanelIdentify(userCred.user.uid);
      
      // Track successful sign-in
      trackEvent('User_SignIn_Success', { 
        email: emailArg
      });
      mixpanelTrack('User_SignIn_Success', { email: emailArg });
    } catch (e: any) {
      setError(e);
      
      // Track sign-in failure
      trackEvent('User_SignIn_Failed', { 
        email: emailArg,
        errorCode: e.code,
        errorMessage: e.message
      });
      trackException(e, { context: 'signIn' });
      
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const sendPasswordReset = async (emailArg: string) => {
    try {
      await sendPasswordResetEmail(auth, emailArg);
      trackEvent('User_PasswordReset_Requested', { email: emailArg });
    } catch (e: any) {
      trackEvent('User_PasswordReset_Failed', { email: emailArg, errorCode: e.code });
      trackException(e, { context: 'sendPasswordReset' });
      throw e;
    }
  };

  const signOut = async () => {
    setLoading(true);
    setError(null);
    try {
      // Track sign-out before clearing state
      trackEvent('User_SignOut', { 
        email: email || 'unknown'
      });
      
      await fbSignOut(auth);
      mixpanelReset();
      setToken(null);
      setEmail(null);
      clearAuthenticatedUserContext();
      resetClaimSync();
    } catch (e: any) {
      setError(e);
      trackException(e, { context: 'signOut' });
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{ token, email, signUp, signIn, signOut, sendPasswordReset, loading, error }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
